import { cn } from '@/renderer/lib/utils';
import { DatePicker } from '@components/common/DatePicker';
import { useAppSelector } from '@hooks/useRedux';
import { selectAccommodationProperty } from '@store/accommodation';
import { selectOutletId } from '@store/auth/authSelectors';
import { Download, Play, RefreshCw } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import {
  NightAuditReportRow,
  fetchNightAuditReport,
  runNightAudit,
} from '../../../../services/accommodationService';
import { apiError, apiSuccess } from '../../../../util/notify';
import {
  currentMonthRange,
  exportToCsv,
  fmtAmount,
  fmtDate,
} from './reportUtils';

const { from: initFrom, to: initTo } = currentMonthRange();

interface DayRow {
  date: string;
  checkIns: number;
  checkOuts: number;
  revenue: number;
  collected: number;
}

export const NightAuditReportPage: React.FC = () => {
  const property = useAppSelector(selectAccommodationProperty);
  const userOutletId = useAppSelector(selectOutletId);
  const outletId = property?.outlet_id ?? userOutletId ?? null;
  const currency = property?.currency ?? '';

  const [dateFrom, setDateFrom] = useState(initFrom);
  const [dateTo, setDateTo] = useState(initTo);
  const [rows, setRows] = useState<NightAuditReportRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const [auditDate, setAuditDate] = useState(today);
  const [isRunningAudit, setIsRunningAudit] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchNightAuditReport(outletId, dateFrom, dateTo);
      setRows(data);
      setLoaded(true);
    } catch (err) {
      apiError(err instanceof Error ? err.message : 'Failed to load report');
    } finally {
      setIsLoading(false);
    }
  }, [outletId, dateFrom, dateTo]);

  // Merge check-in and check-out rows into one row per day
  const dayRows = useMemo<DayRow[]>(() => {
    const map = new Map<string, DayRow>();
    rows.forEach((r) => {
      if (!map.has(r.audit_date)) {
        map.set(r.audit_date, {
          date: r.audit_date,
          checkIns: 0,
          checkOuts: 0,
          revenue: 0,
          collected: 0,
        });
      }
      const day = map.get(r.audit_date)!;
      if (r.event_type === 'check_in') {
        day.checkIns += r.booking_count;
        day.revenue += r.revenue ?? 0;
        day.collected += r.collected ?? 0;
      } else {
        day.checkOuts += r.booking_count;
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.date.localeCompare(b.date),
    );
  }, [rows]);

  const totalCheckIns = dayRows.reduce((s, r) => s + r.checkIns, 0);
  const totalCheckOuts = dayRows.reduce((s, r) => s + r.checkOuts, 0);
  const totalRevenue = dayRows.reduce((s, r) => s + r.revenue, 0);
  const totalCollected = dayRows.reduce((s, r) => s + r.collected, 0);

  const handleRunAudit = useCallback(async () => {
    if (!property?.id) {
      apiError('No property selected');
      return;
    }
    setIsRunningAudit(true);
    try {
      const result = await runNightAudit(property.id, auditDate);
      apiSuccess(
        `Night audit complete — ${result.processed} booking(s) processed for ${auditDate}`,
      );
    } catch (err) {
      apiError(err instanceof Error ? err.message : 'Night audit failed');
    } finally {
      setIsRunningAudit(false);
    }
  }, [property?.id, auditDate]);

  const handleCsv = () => {
    exportToCsv(
      `night-audit-${dateFrom}-${dateTo}`,
      dayRows.map((r) => ({
        Date: r.date,
        'Check-ins': r.checkIns,
        'Check-outs': r.checkOuts,
        [`Revenue (${currency})`]: fmtAmount(r.revenue),
        [`Collected (${currency})`]: fmtAmount(r.collected),
      })),
    );
  };

  return (
    <div className="space-y-4">
      {/* Run Night Audit */}
      <div className="rounded-2xl border border-pos-primary/20 bg-pos-primary/5 p-4">
        <div className="mb-3">
          <h4 className="text-sm font-semibold text-pos-primary">
            Run Night Audit
          </h4>
          <p className="mt-0.5 text-xs text-gray-600">
            Recognises room revenue for all checked-in bookings on the selected
            date. Safe to re-run — already-processed dates are skipped.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <DatePicker
            mode="single"
            id="na-audit-date"
            label="Stay date"
            value={auditDate}
            onChange={setAuditDate}
          />
          <button
            type="button"
            onClick={handleRunAudit}
            disabled={isRunningAudit || !property?.id}
            className="flex items-center gap-2 rounded-xl bg-pos-primary px-4 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:opacity-60"
          >
            <Play size={14} className={isRunningAudit ? 'animate-pulse' : ''} />
            {isRunningAudit ? 'Running…' : 'Run night audit'}
          </button>
        </div>
      </div>

      {/* Report Controls */}
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-gray-200 bg-white p-4">
        <DatePicker
          mode="single"
          id="na-date-from"
          label="From date"
          value={dateFrom}
          onChange={setDateFrom}
        />
        <DatePicker
          mode="single"
          id="na-date-to"
          label="To date"
          value={dateTo}
          onChange={setDateTo}
        />
        <button
          type="button"
          onClick={load}
          disabled={isLoading}
          className="flex items-center gap-2 rounded-xl bg-pos-primary px-4 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:opacity-60"
        >
          <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          {isLoading ? 'Loading…' : 'Run report'}
        </button>
        {loaded && dayRows.length > 0 && (
          <button
            type="button"
            onClick={handleCsv}
            className="flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Download size={14} />
            Export CSV
          </button>
        )}
      </div>

      {loaded && (
        <>
          {/* KPI summary */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Days', value: String(dayRows.length), sub: 'in range' },
              {
                label: 'Total Check-ins',
                value: String(totalCheckIns),
                sub: 'arrivals',
              },
              {
                label: 'Total Check-outs',
                value: String(totalCheckOuts),
                sub: 'departures',
              },
              {
                label: 'Revenue',
                value: `${currency} ${fmtAmount(totalRevenue)}`,
                sub: `Collected: ${currency} ${fmtAmount(totalCollected)}`,
              },
            ].map((card) => (
              <div
                key={card.label}
                className="rounded-2xl border border-gray-200 bg-white p-4"
              >
                <p className="text-xs text-gray-500">{card.label}</p>
                <p className="mt-1 text-xl font-semibold text-gray-900">
                  {card.value}
                </p>
                <p className="mt-0.5 text-xs text-gray-400">{card.sub}</p>
              </div>
            ))}
          </div>

          {/* Night audit table */}
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-5 py-3">
              <h4 className="text-sm font-semibold text-gray-900">
                Night audit — {fmtDate(dateFrom)} to {fmtDate(dateTo)}
              </h4>
            </div>
            {dayRows.length === 0 ? (
              <div className="flex h-28 items-center justify-center text-sm text-gray-400">
                No activity found for this period.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-gray-100 bg-gray-50">
                    <tr>
                      {[
                        'Date',
                        'Check-ins',
                        'Check-outs',
                        `Revenue (${currency})`,
                        `Collected (${currency})`,
                      ].map((h) => (
                        <th
                          key={h}
                          className={cn(
                            'px-4 py-2.5 text-xs font-semibold text-gray-500',
                            [
                              `Revenue (${currency})`,
                              `Collected (${currency})`,
                            ].includes(h)
                              ? 'text-right'
                              : 'text-left',
                          )}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {dayRows.map((r) => (
                      <tr key={r.date} className="hover:bg-gray-50/50">
                        <td className="px-4 py-2.5 font-medium text-gray-900">
                          {fmtDate(r.date)}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="font-medium text-green-600">
                            {r.checkIns}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="font-medium text-blue-600">
                            {r.checkOuts}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right font-medium text-gray-900">
                          {r.revenue > 0 ? fmtAmount(r.revenue) : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right text-gray-700">
                          {r.collected > 0 ? fmtAmount(r.collected) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t-2 border-gray-200 bg-gray-50">
                    <tr>
                      <td className="px-4 py-2.5 text-xs font-semibold text-gray-600">
                        Total ({dayRows.length} days)
                      </td>
                      <td className="px-4 py-2.5 font-bold text-green-600">
                        {totalCheckIns}
                      </td>
                      <td className="px-4 py-2.5 font-bold text-blue-600">
                        {totalCheckOuts}
                      </td>
                      <td className="px-4 py-2.5 text-right text-sm font-bold text-gray-900">
                        {fmtAmount(totalRevenue)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-sm font-semibold text-gray-700">
                        {fmtAmount(totalCollected)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {!loaded && !isLoading && (
        <div className="flex h-40 items-center justify-center rounded-2xl border border-dashed border-gray-200 text-sm text-gray-400">
          Select a date range and click &ldquo;Run report&rdquo; to load data.
        </div>
      )}
    </div>
  );
};
